import React from 'react';
import { ArrowRight, CheckCircle, Smartphone, Zap, Shield, Users, Globe, Star } from 'lucide-react';
import Layout from '../../layouts/Layout';
import CTA from '../../components/CTA';
import { Link } from 'react-router-dom';

const MobileDevelopmentPage = () => {
    const platforms = [
        {
            title: "React Native",
            description: "Cross-platform development for iOS and Android with native performance",
            features: ["Single codebase", "Native performance", "Hot reloading", "Large ecosystem"]
        },
        {
            title: "Native iOS",
            description: "Swift-based applications leveraging the full power of Apple's ecosystem",
            features: ["Swift & SwiftUI", "iOS-specific features", "App Store optimised", "Metal performance"]
        },
        {
            title: "Native Android",
            description: "Kotlin applications designed for the diverse Android ecosystem",
            features: ["Kotlin & Jetpack", "Material Design", "Google Play ready", "Android-specific APIs"]
        },
        {
            title: "Flutter",
            description: "Google's UI toolkit for beautiful, fast apps on any platform",
            features: ["Single codebase", "Custom widgets", "High performance", "Hot reload"]
        }
    ];

    const features = [
        {
            icon: <Zap className="w-6 h-6" />,
            title: "Native Performance",
            description: "Optimised for speed and efficiency, delivering smooth animations and responsive interactions that users expect."
        },
        {
            icon: <Shield className="w-6 h-6" />,
            title: "Secure by Design",
            description: "Built-in security features including data encryption, secure authentication, and compliance with mobile security standards."
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "User-Centric Design",
            description: "Intuitive interfaces following platform-specific design guidelines for optimal user experience and engagement."
        },
        {
            icon: <Globe className="w-6 h-6" />,
            title: "Cross-Platform Ready",
            description: "Reach your entire audience with applications that work seamlessly across iOS, Android, and other platforms."
        }
    ];

    const capabilities = [
        {
            category: "Core Features",
            items: [
                "Custom UI/UX Design",
                "Real-time Data Synchronisation", 
                "Push Notifications",
                "Offline Functionality",
                "Biometric Authentication",
                "In-App Purchases"
            ]
        },
        {
            category: "Integrations",
            items: [
                "Payment Gateways",
                "Social Media APIs",
                "Cloud Services (AWS, Firebase)",
                "Third-party SDKs",
                "Analytics Platforms",
                "CRM Systems"
            ]
        },
        {
            category: "Advanced Features", 
            items: [
                "Augmented Reality (AR)",
                "Machine Learning Integration",
                "Geolocation Services",
                "Camera & Media Processing",
                "Bluetooth & IoT Connectivity",
                "Voice Recognition"
            ]
        },
        {
            category: "Performance & Security",
            items: [
                "Code Obfuscation",
                "SSL Certificate Pinning",
                "Performance Monitoring",
                "Crash Analytics",
                "Battery Optimisation",
                "Memory Management"
            ]
        }
    ];

    const process = [
        {
            step: "01",
            title: "Strategy & Planning",
            description: "We analyse your target audience, business objectives, and technical requirements to create a comprehensive mobile strategy."
        },
        {
            step: "02",
            title: "UI/UX Design",
            description: "Our designers create intuitive, platform-specific interfaces that provide exceptional user experiences across all devices."
        },
        {
            step: "03",
            title: "Development & Testing",
            description: "Using agile methodologies, we build your app with continuous testing on real devices to ensure quality and performance."
        },
        {
            step: "04",
            title: "App Store Deployment",
            description: "We handle the complete submission process for both Apple App Store and Google Play Store, ensuring compliance with all guidelines."
        },
        {
            step: "05",
            title: "Maintenance & Updates",
            description: "Ongoing support including OS updates, bug fixes, feature enhancements, and performance monitoring to keep your app current."
        }
    ];

    const appTypes = [
        {
            title: "Enterprise Applications",
            description: "Internal business apps for productivity, communication, and workflow management",
            examples: ["Employee portals", "Field service apps", "Inventory management", "CRM mobile apps"]
        },
        {
            title: "E-commerce Apps",
            description: "Feature-rich shopping experiences with payment integration and user engagement",
            examples: ["Retail apps", "Marketplace platforms", "B2B ordering systems", "Subscription services"]
        },
        {
            title: "FinTech Solutions",
            description: "Secure financial applications with advanced security and compliance features",
            examples: ["Banking apps", "Investment platforms", "Payment solutions", "Cryptocurrency wallets"]
        },
        {
            title: "Healthcare & Wellness",
            description: "HIPAA-compliant applications for healthcare providers and wellness tracking",
            examples: ["Patient portals", "Telemedicine apps", "Fitness trackers", "Medical records"]
        }
    ];

    const benefits = [
        "Native and cross-platform development expertise",
        "App Store and Google Play submission assistance",
        "Post-launch analytics and performance monitoring", 
        "Regular updates for new OS versions and features",
        "Integration with existing web services and APIs",
        "Comprehensive testing on multiple devices and OS versions",
        "Offline functionality for uninterrupted user experience",
        "Push notification campaigns and user engagement tools"
    ];

    return (
        <Layout
            title="Mobile App Development Services"
            description="Professional mobile app development in the UK. Native iOS, Android, and cross-platform React Native applications built for performance, security, and user engagement."
            keywords="mobile app development UK, iOS development, Android development, React Native development, Flutter development, native mobile apps"
            path="/services/mobile-development"
        >
            <div className="min-h-screen bg-white">
                {/* Hero Section */}
                <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <div className="inline-flex items-center px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-medium mb-8">
                                    <Smartphone className="w-4 h-4 mr-2" />
                                    Mobile Development
                                </div>
                                
                                <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-tight">
                                    Native & Cross-Platform
                                    <span className="block text-slate-700 mt-2">Mobile Applications</span>
                                </h1>
                                
                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    Transform your business with custom mobile applications that engage users and drive growth. 
                                    We specialise in React Native, native iOS, Android, and Flutter development with enterprise-grade 
                                    security and performance.
                                </p>
                                
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Link to="/contact" className="bg-slate-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800 transition-all hover:shadow-lg flex items-center justify-center">
                                        Start Your App Project <ArrowRight className="ml-2 w-5 h-5" />
                                    </Link>
                                    <Link to="/portfolio" className="border-2 border-slate-200 text-slate-700 px-8 py-4 rounded-xl font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all">
                                        View Mobile Portfolio
                                    </Link>
                                </div>
                            </div>
                            
                            <div className="relative">
                                <div className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-200">
                                    <div className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">App Store Rating</span>
                                            <div className="flex items-center">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <Star key={star} className="w-4 h-4 text-yellow-400 fill-current" />
                                                ))}
                                                <span className="ml-2 text-slate-900 font-semibold">5.0</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Download Success Rate</span>
                                            <span className="text-slate-900 font-semibold">98.5%</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Crash-Free Sessions</span>
                                            <span className="text-slate-900 font-semibold">99.9%</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">User Retention (30 days)</span>
                                            <span className="text-slate-900 font-semibold">85%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Platforms Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Development Platforms</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Choose the right technology stack for your mobile application needs
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-8">
                            {platforms.map((platform, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{platform.title}</h3>
                                    <p className="text-slate-600 mb-6 leading-relaxed">{platform.description}</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        {platform.features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                                <span className="text-slate-700 text-sm">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Why Choose Our Mobile Development?</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                We deliver mobile applications that users love and businesses rely on
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {features.map((feature, index) => (
                                <div key={index} className="bg-white rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mb-6">
                                        {feature.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-4">{feature.title}</h3>
                                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Capabilities Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Mobile App Capabilities</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Comprehensive features and integrations for modern mobile applications
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {capabilities.map((capability, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                                    <h3 className="text-lg font-bold text-slate-900 mb-4">{capability.category}</h3>
                                    <div className="space-y-2">
                                        {capability.items.map((item, itemIndex) => (
                                            <div key={itemIndex} className="flex items-center">
                                                <div className="w-2 h-2 bg-slate-900 rounded-full mr-3"></div>
                                                <span className="text-slate-700 text-sm">{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* App Types Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Types of Mobile Applications</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                We specialise in developing various types of mobile applications across industries
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-8">
                            {appTypes.map((type, index) => (
                                <div key={index} className="bg-white rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{type.title}</h3>
                                    <p className="text-slate-600 mb-6 leading-relaxed">{type.description}</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        {type.examples.map((example, exampleIndex) => (
                                            <div key={exampleIndex} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                                <span className="text-slate-700 text-sm">{example}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Process Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Mobile Development Process</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                From concept to App Store, we guide you through every step of mobile app development
                            </p>
                        </div>
                        
                        <div className="space-y-8">
                            {process.map((step, index) => (
                                <div key={index} className="flex items-start">
                                    <div className="flex-shrink-0 w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white font-bold text-lg mr-6">
                                        {step.step}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-bold text-slate-900 mb-4">{step.title}</h3>
                                        <p className="text-lg text-slate-600 leading-relaxed">{step.description}</p>
                                    </div>
                                    {/* {index < process.length - 1 && (
                                        <ChevronRight className="w-6 h-6 text-slate-400 ml-6" />
                                    )} */}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <div>
                                <h2 className="text-4xl font-bold text-slate-900 mb-8">What You Get</h2>
                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    Our mobile development services provide end-to-end solutions that deliver exceptional 
                                    user experiences and drive business growth.
                                </p>
                                
                                <div className="space-y-4">
                                    {benefits.map((benefit, index) => (
                                        <div key={index} className="flex items-start">
                                            <CheckCircle className="w-6 h-6 text-green-600 mr-4 flex-shrink-0 mt-0.5" />
                                            <span className="text-slate-700 text-lg">{benefit}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
                                <h3 className="text-2xl font-bold text-slate-900 mb-6">App Store Success</h3>
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">App Store Approval Rate</span>
                                        <span className="text-slate-900 font-semibold">100%</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Average Review Rating</span>
                                        <div className="flex items-center">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <Star key={star} className="w-4 h-4 text-yellow-400 fill-current" />
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Time to Market</span>
                                        <span className="text-slate-900 font-semibold">8-16 weeks</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Post-Launch Support</span>
                                        <span className="text-slate-900 font-semibold">Included</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Performance Stats */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="bg-slate-900 rounded-3xl p-12 text-white">
                            <div className="text-center mb-12">
                                <h2 className="text-4xl font-bold mb-6">Mobile App Performance</h2>
                                <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                                    Our mobile applications consistently deliver exceptional performance and user satisfaction
                                </p>
                            </div>
                            
                            <div className="grid md:grid-cols-4 gap-8 text-center">
                                <div>
                                    <div className="text-4xl font-bold mb-2">99.9%</div>
                                    <div className="text-slate-300">Crash-Free Rate</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">&lt;3s</div>
                                    <div className="text-slate-300">App Launch Time</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">85%</div>
                                    <div className="text-slate-300">30-Day Retention</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">4.8+</div>
                                    <div className="text-slate-300">App Store Rating</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <CTA />
            </div>
        </Layout>
    );
};

export default MobileDevelopmentPage;